import React from 'react';
import Modal from '../../../components/base/Modal';
import ViewModeToggle from '../../../components/common/ViewModeToggle';
import InputLabel from '../../../components/base/InputLabel';
import NumberInput from '../../../components/base/NumberInput';
import MultiTpSettings from '../../../components/metrics/MultiTpSettings';
import BigNumber from 'bignumber.js';
import { ERROR_MESSAGES } from '../../../errors/constants';
import { MIN_VALUES } from '../../../constants/minValues';

const formatFeeResult = (value) => {
  if (!value || value === '--') return '--';
  try {
    const num = parseFloat(value);
    if (isNaN(num)) return '--';
    return `$${num.toFixed(2)}`;
  } catch {
    return '--';
  }
};

const MicroLabel = ({ children }) => (
  <span className="text-slate-600 text-[10px] block mb-1">{children}</span>
);

const StocksSettingsModal = ({ isOpen, onClose, values, setValues, errors = {}, initialFocusField, metrics = {}, viewMode, onViewModeChange }) => {
  const feeBreakdown = metrics.feeBreakdown || {};

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Settings"
      wide
    >
      <div className="space-y-6 pb-10">
        {/* View mode — Full / Compact (moved here from the metrics card) */}
        <div className="flex items-center justify-between">
          <InputLabel>View</InputLabel>
          <ViewModeToggle viewMode={viewMode} onChange={onViewModeChange} />
        </div>

        {/* Risk and Account Balance */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="flex justify-between mb-2">
              <InputLabel required>Risk</InputLabel>
              <div className="flex gap-1">
                <button
                  onClick={() => setValues(prev => ({ ...prev, riskUnit: 'percent' }))}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${values.riskUnit === 'percent'
                    ? 'bg-primary-orange/20 text-primary-orange'
                    : 'hover:bg-slate-800 text-slate-500'
                    }`}
                >
                  %
                </button>
                <button
                  onClick={() => setValues(prev => ({ ...prev, riskUnit: 'fixed' }))}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${values.riskUnit === 'fixed'
                    ? 'bg-primary-orange/20 text-primary-orange'
                    : 'hover:bg-slate-800 text-slate-500'
                    }`}
                >
                  $
                </button>
              </div>
            </div>
            <NumberInput
              value={values.risk}
              onChange={(value) => setValues(prev => ({
                ...prev,
                risk: value || new BigNumber(0)
              }))}
              suffix={values.riskUnit === 'percent' ? '%' : undefined}
              prefix={values.riskUnit === 'fixed' ? '$' : undefined}
              decimals={2}
              required
              min={MIN_VALUES.risk}
              error={errors.risk ? { message: ERROR_MESSAGES.INVALID_RISK } : null}
            />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <InputLabel required>Account Balance</InputLabel>
              <div className="h-[28px]"></div>
            </div>
            <NumberInput
              value={values.accountBalance}
              onChange={(value) => setValues(prev => ({
                ...prev,
                accountBalance: value || new BigNumber(0)
              }))}
              prefix="$"
              decimals={2}
              required
              min={MIN_VALUES.accountBalance}
              error={errors.accountBalance ? { message: ERROR_MESSAGES.INVALID_ACCOUNT_BALANCE } : null}
              autoFocus={initialFocusField === 'accountBalance'}
            />
          </div>
        </div>

        {/* R:R and Leverage */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <InputLabel required>Nominal R:R</InputLabel>
            <NumberInput
              value={values.nominalRR}
              onChange={(value) => setValues(prev => ({
                ...prev,
                nominalRR: value || new BigNumber(0)
              }))}
              suffix="x"
              decimals={1}
              required
              min={MIN_VALUES.nominalRR}
              error={errors.nominalRR ? { message: ERROR_MESSAGES.INVALID_RR } : null}
            />
          </div>

          <div>
            <InputLabel required>Max Leverage</InputLabel>
            <NumberInput
              value={values.maxLeverage}
              onChange={(value) => setValues(prev => ({
                ...prev,
                maxLeverage: value || new BigNumber(0)
              }))}
              suffix="x"
              decimals={0}
              required
              min={MIN_VALUES.maxLeverage}
              error={errors.maxLeverage ? { message: ERROR_MESSAGES.INVALID_LEVERAGE } : null}
            />
          </div>
        </div>

        {/* Safety buffer (risk multiple) */}
        <div>
          <InputLabel required>Safety Buffer</InputLabel>
          <NumberInput
            value={values.safetyBufferR}
            onChange={(value) => setValues(prev => ({
              ...prev,
              safetyBufferR: value || MIN_VALUES.safetyBufferR
            }))}
            suffix="R"
            decimals={2}
            step={0.25}
            required
            min={MIN_VALUES.safetyBufferR}
            error={errors.safetyBufferR ? { message: ERROR_MESSAGES.INVALID_MARGIN } : null}
          />
          <p className="text-xs text-slate-500 mt-1.5">
            Keeps liquidation this many stop-distances beyond your stop — 2R = two stops past it.
          </p>
        </div>

        {/* Divider */}
        <div className="border-t border-white/5" />

        {/* FEE BREAKDOWN */}
        <div>
          <div className="text-slate-500 uppercase tracking-[2px] text-[10px] font-bold mb-3">Fee Breakdown</div>

          {/* Fee input grid */}
          <div className="bg-white/[0.025] rounded-[10px] overflow-hidden mb-3">

            {/* Broker Commission */}
            <div className="grid grid-cols-[1fr_96px_72px_66px] items-center gap-x-1.5 px-3.5 py-2.5 border-b border-white/5">
              <div className="flex flex-col gap-1.5">
                <span className="text-white/60 text-[12px]">Broker Commission</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setValues(prev => ({ ...prev, brokerMode: 'perShare' }))}
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${values.brokerMode === 'perShare'
                      ? 'bg-primary-orange/30 text-amber-500'
                      : 'bg-white/[0.08] text-slate-500 hover:text-slate-400'
                      }`}
                  >
                    Per Share
                  </button>
                  <button
                    onClick={() => setValues(prev => ({ ...prev, brokerMode: 'fixed' }))}
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${values.brokerMode === 'fixed'
                      ? 'bg-primary-orange/30 text-amber-500'
                      : 'bg-white/[0.08] text-slate-500 hover:text-slate-400'
                      }`}
                  >
                    Fixed
                  </button>
                </div>
              </div>
              <div>
                <MicroLabel>Rate</MicroLabel>
                <NumberInput
                  value={values.brokerRate}
                  onChange={(value) => setValues(prev => ({ ...prev, brokerRate: value || new BigNumber(0) }))}
                  prefix="$"
                  decimals={values.brokerMode === 'perShare' ? 4 : 2}
                  showSpinners={false}
                  autoFocus={initialFocusField === 'fees'}
                />
              </div>
              {values.brokerMode === 'perShare' ? (
                <div>
                  <MicroLabel>Min $</MicroLabel>
                  <NumberInput
                    value={values.brokerMin}
                    onChange={(value) => setValues(prev => ({ ...prev, brokerMin: value || new BigNumber(0) }))}
                    prefix="$"
                    decimals={2}
                    showSpinners={false}
                  />
                </div>
              ) : <div />}
              {values.brokerMode === 'perShare' ? (
                <div>
                  <MicroLabel>Max %</MicroLabel>
                  <NumberInput
                    value={values.brokerMaxPct}
                    onChange={(value) => setValues(prev => ({ ...prev, brokerMaxPct: value || new BigNumber(0) }))}
                    suffix="%"
                    decimals={1}
                    showSpinners={false}
                  />
                </div>
              ) : <div />}
            </div>

            {/* Exchange / ECN */}
            <div className="grid grid-cols-[1fr_96px_72px_66px] items-center gap-x-1.5 px-3.5 py-2.5 border-b border-white/5">
              <span className="text-white/60 text-[12px]">Exchange / ECN</span>
              <div>
                <MicroLabel>$/share</MicroLabel>
                <NumberInput
                  value={values.exchangeRate}
                  onChange={(value) => setValues(prev => ({ ...prev, exchangeRate: value || new BigNumber(0) }))}
                  prefix="$"
                  decimals={4}
                  showSpinners={false}
                />
              </div>
              <div />
              <div />
            </div>

            {/* Clearing Fee */}
            <div className="grid grid-cols-[1fr_96px_72px_66px] items-center gap-x-1.5 px-3.5 py-2.5 border-b border-white/5">
              <span className="text-white/60 text-[12px]">Clearing Fee</span>
              <div>
                <MicroLabel>$/share</MicroLabel>
                <NumberInput
                  value={values.clearingRate}
                  onChange={(value) => setValues(prev => ({ ...prev, clearingRate: value || new BigNumber(0) }))}
                  prefix="$"
                  decimals={4}
                  showSpinners={false}
                />
              </div>
              <div>
                <MicroLabel>Max %</MicroLabel>
                <NumberInput
                  value={values.clearingMaxPct}
                  onChange={(value) => setValues(prev => ({ ...prev, clearingMaxPct: value || new BigNumber(0) }))}
                  suffix="%"
                  decimals={1}
                  showSpinners={false}
                />
              </div>
              <div />
            </div>

            {/* SEC Fee */}
            <div className="grid grid-cols-[1fr_96px_72px_66px] items-center gap-x-1.5 px-3.5 py-2.5 border-b border-white/5">
              <span className="text-white/60 text-[12px]">SEC Fee</span>
              <div>
                <MicroLabel>$ per $1M sold</MicroLabel>
                <NumberInput
                  value={values.secRate}
                  onChange={(value) => setValues(prev => ({ ...prev, secRate: value || new BigNumber(0) }))}
                  prefix="$"
                  decimals={2}
                  showSpinners={false}
                />
              </div>
              <div />
              <div />
            </div>

            {/* FINRA TAF */}
            <div className="grid grid-cols-[1fr_96px_72px_66px] items-center gap-x-1.5 px-3.5 py-2.5 border-b border-white/5">
              <span className="text-white/60 text-[12px]">FINRA TAF</span>
              <div>
                <MicroLabel>$ per 1K shares</MicroLabel>
                <NumberInput
                  value={values.finraRate}
                  onChange={(value) => setValues(prev => ({ ...prev, finraRate: value || new BigNumber(0) }))}
                  prefix="$"
                  decimals={3}
                  showSpinners={false}
                />
              </div>
              <div>
                <MicroLabel>Max $</MicroLabel>
                <NumberInput
                  value={values.finraMax}
                  onChange={(value) => setValues(prev => ({ ...prev, finraMax: value || new BigNumber(0) }))}
                  prefix="$"
                  decimals={2}
                  showSpinners={false}
                />
              </div>
              <div />
            </div>

            {/* FINRA CAT */}
            <div className="grid grid-cols-[1fr_96px_72px_66px] items-center gap-x-1.5 px-3.5 py-2.5">
              <span className="text-white/60 text-[12px]">FINRA CAT</span>
              <div>
                <MicroLabel>$ per 1K shares</MicroLabel>
                <NumberInput
                  value={values.finraCatRate}
                  onChange={(value) => setValues(prev => ({ ...prev, finraCatRate: value || new BigNumber(0) }))}
                  prefix="$"
                  decimals={3}
                  showSpinners={false}
                />
              </div>
              <div />
              <div />
            </div>

          </div>

          {/* Fee Results (live calculated) */}
          <div className="bg-white/[0.025] rounded-[10px] overflow-hidden mt-3">
            <div className="flex justify-between items-center px-3.5 py-2 border-b border-white/5">
              <span className="text-slate-500 text-[12px]">Broker Commission</span>
              <span className="text-slate-300/80 text-[13px] font-medium min-w-[64px] text-right font-mono">{formatFeeResult(feeBreakdown.broker)}</span>
            </div>
            <div className="flex justify-between items-center px-3.5 py-2 border-b border-white/5">
              <span className="text-slate-500 text-[12px]">Exchange / ECN</span>
              <span className="text-slate-300/80 text-[13px] font-medium min-w-[64px] text-right font-mono">{formatFeeResult(feeBreakdown.exchange)}</span>
            </div>
            <div className="flex justify-between items-center px-3.5 py-2 border-b border-white/5">
              <span className="text-slate-500 text-[12px]">Clearing Fee</span>
              <span className="text-slate-300/80 text-[13px] font-medium min-w-[64px] text-right font-mono">{formatFeeResult(feeBreakdown.clearing)}</span>
            </div>
            <div className="flex justify-between items-center px-3.5 py-2 border-b border-white/5">
              <span className="text-slate-500 text-[12px]">SEC Fee</span>
              <span className="text-slate-300/80 text-[13px] font-medium min-w-[64px] text-right font-mono">{formatFeeResult(feeBreakdown.sec)}</span>
            </div>
            <div className="flex justify-between items-center px-3.5 py-2 border-b border-white/5">
              <span className="text-slate-500 text-[12px]">FINRA TAF</span>
              <span className="text-slate-300/80 text-[13px] font-medium min-w-[64px] text-right font-mono">{formatFeeResult(feeBreakdown.finra)}</span>
            </div>
            <div className="flex justify-between items-center px-3.5 py-2 border-b border-white/[0.08]">
              <span className="text-slate-500 text-[12px]">FINRA CAT</span>
              <span className="text-slate-300/80 text-[13px] font-medium min-w-[64px] text-right font-mono">{formatFeeResult(feeBreakdown.finraCat)}</span>
            </div>
            <div className="flex justify-between items-center px-3.5 py-2.5">
              <span className="text-white font-bold text-[13px]">Total Round Turn</span>
              <span className="text-amber-500 text-[15px] font-extrabold min-w-[64px] text-right font-mono">{formatFeeResult(feeBreakdown.total)}</span>
            </div>
          </div>

        </div>

        <MultiTpSettings values={values} onChange={(p) => setValues((prev) => ({ ...prev, ...p }))} />
      </div>
    </Modal>
  );
};

export default StocksSettingsModal;
